// @ts-check
import { useEffect, useRef } from "react";

import { NotificationType } from "@transfr-inc/dashboard-components";

import { useNotificationContext } from "@contexts/notification-context";
import { trackOutcomesRawDataDownloaded } from "@lib/event-trackers";
import { defaultErrorMessage } from "@lib/http-client";
import { usePolledJob } from "@lib/hooks/use-polled-job";
import { exportGranularity } from "@views/outcomes/components/raw-data-export.modal";
import { useExportStatus, useStartCsvExport } from "@queries/outcomes";
import { useContainer } from "@src/contexts/container-context";
import { useOutcomesUIStore } from "@src/stores/zustand/outcomes-ui.store";

import "./csv-export.scss";

/**
 * Client-side ceiling on how long a single export is polled. The server times a
 * job out at 600s; this 12-minute ceiling only guards a status endpoint that
 * never reaches a terminal state, so polling cannot run forever. The ceiling is
 * measured from when polling (re)starts, so a page refresh restarts the clock.
 */
const MAX_POLL_MS = 12 * 60 * 1000;

/**
 * How many consecutive failed status-poll cycles are tolerated before an export
 * is abandoned. A transient blip (a stray 5xx or a brief network drop) is polled
 * through and self-heals on the next success; only a streak this long is treated
 * as a genuinely failed poll and surfaced as an error.
 */
const STATUS_ERROR_TOLERANCE = 3;

/**
 * sessionStorage key holding the in-flight export job. The job is session-scoped
 * on purpose: it survives a page refresh so the toast and polling resume, but
 * not the end of the browsing session. This mirrors the outcomes learner-filter
 * persistence.
 */
const EXPORT_JOB_STORAGE_KEY = "transfr:dashboard:outcomeExportJob";

const GENERATING_MESSAGE =
  "Your report is generating. This may take a few minutes.";
const TIMEOUT_MESSAGE =
  "Your report is taking longer than expected. Please try again.";

/**
 * The backend detail on a rejected submit, surfaced verbatim so the caller sees
 * the real reason (e.g. the metric-empty or per-simulation 400), falling back to
 * a generic message.
 *
 * @param {any} error
 * @returns {string}
 */
function backendDetail(error) {
  return error?.data?.detail ?? error?.data?.message ?? defaultErrorMessage;
}

/**
 * Headless controller for the CSV export lifecycle. Given an export `request`
 * (assembled by a scope view when a level is picked and handed over through the
 * outcomes UI store), it POSTs the job and adapts the shared job-polling
 * lifecycle ({@link usePolledJob}) onto the notification surface: an in-progress
 * toast while polling, then a terminal toast.
 *
 * A new `request` object reference starts a new export, and is taken off the
 * store once submitted. It is mounted at the app root, so polling and the toast
 * survive navigating between pages, and it resumes a job left in flight after a
 * refresh. The app shell around it can still remount mid-session (it is rebuilt
 * whenever the current-user cache is rewritten, e.g. by another tab), so a
 * request left on the store would be submitted — and its outcome toasted — a
 * second time.
 *
 * The lifecycle:
 * - submit → a persistent, non-dismissible "generating" toast with a spinner,
 *   or an error toast surfacing the backend 400; the job id is persisted so a
 *   refresh resumes it;
 * - `ready` → the generating toast is replaced by a dismissible success toast
 *   carrying a Download link; the file downloads only when that link is clicked
 *   (nothing downloads on its own, so a resumed job never surprises the user);
 * - `failed` → dismissible error toast surfacing `error_message`;
 * - a status poll that keeps failing past the tolerance → dismissible generic
 *   error toast;
 * - past the poll ceiling → dismissible timeout error toast.
 *
 * @param {object} props
 * @param {{
 *   level: string,
 *   orgCode: string,
 *   filters: object,
 *   metricIds: string[],
 *   fallbackName: string,
 * } | null} [props.request] - The export to run; a new reference triggers a run.
 */
export function CsvExportView({ request }) {
  const { outcomesService } = useContainer();
  const setExportRequest = useOutcomesUIStore(
    (state) => state.setExportRequest
  );
  // NotificationContext is an untyped ({}) React context; treat it as its known
  // runtime shape.
  const { addNotification, closeNotification } =
    /** @type {{ addNotification: Function, closeNotification: Function }} */ (
      useNotificationContext()
    );

  // The fallback filename assembled at submit time, read back when the job is
  // handed to the lifecycle so it can be persisted as the job's `meta`.
  const fallbackNameRef =
    /** @type {import("react").MutableRefObject<string|undefined>} */ (
      useRef()
    );
  // Only the job id and filename are persisted, so a job resumed after a refresh
  // reports no grain rather than guessing one.
  const levelRef =
    /** @type {import("react").MutableRefObject<string|undefined>} */ (
      useRef()
    );
  // Id of the persistent "generating" toast, so it can be closed on a terminal
  // outcome. Held in a ref because it is set and read across callbacks.
  const loadingToastIdRef = useRef(/** @type {string|null} */ (null));

  const closeLoadingToast = () => {
    if (loadingToastIdRef.current) {
      closeNotification({ id: loadingToastIdRef.current });
      loadingToastIdRef.current = null;
    }
  };

  const notifyDismissible = (type, message) =>
    addNotification({ type, message, closable: true, autoClose: false });

  // The success toast carries the download trigger: the file is fetched only
  // when the link is clicked, so a resumed job never downloads unprompted.
  const reportDownloaded = (succeeded) =>
    trackOutcomesRawDataDownloaded("outcomes_raw_data_download", {
      rawDataGranularity: exportGranularity(levelRef.current),
      hasDataDownloadedSuccessfully: succeeded,
    });

  const notifyReady = (readyJobId, readyName) => {
    const onDownload = () => {
      outcomesService
        .downloadExport(readyJobId, readyName)
        .then(() => reportDownloaded(true))
        .catch(() => {
          reportDownloaded(false);
          notifyDismissible(NotificationType.error, defaultErrorMessage);
        });
    };
    addNotification({
      type: NotificationType.success,
      closable: true,
      autoClose: false,
      message: (
        <span>
          Your report is ready.{" "}
          <button
            type="button"
            className="csv-export-download-link"
            onClick={onDownload}
          >
            Download
          </button>
        </span>
      ),
    });
  };

  const { track } = usePolledJob({
    useStatus: useExportStatus,
    // The export status endpoint reports a terminal `ready`/`failed`; any other
    // value is a non-terminal in-progress status.
    getStatus: (data) => /** @type {any} */ (data)?.status,
    storageKey: EXPORT_JOB_STORAGE_KEY,
    tolerance: STATUS_ERROR_TOLERANCE,
    ceilingMs: MAX_POLL_MS,
    onStart: () => {
      loadingToastIdRef.current = addNotification({
        type: NotificationType.info,
        message: GENERATING_MESSAGE,
        icon: ["fa-solid", "spinner"],
        iconConfig: { spin: true },
        closable: false,
        autoClose: false,
        // Without a close button this toast has no trailing element, so the
        // message needs its own right padding (see csv-export.scss).
        className: "csv-export-generating",
      });
    },
    onSettled: ({ outcome, jobId, data, meta }) => {
      closeLoadingToast();
      switch (outcome) {
        case "ready": {
          notifyReady(jobId, meta);
          break;
        }
        case "failed": {
          notifyDismissible(
            NotificationType.error,
            /** @type {any} */ (data)?.error_message ?? defaultErrorMessage
          );
          break;
        }
        case "timeout": {
          notifyDismissible(NotificationType.error, TIMEOUT_MESSAGE);
          break;
        }
        default: {
          notifyDismissible(NotificationType.error, defaultErrorMessage);
        }
      }
    },
  });

  const { mutate: startExport } = useStartCsvExport({
    onSuccess: (data) => {
      const id = data?.job_id ?? null;
      if (!id) {
        // A submit that reports success but names no job can never be polled to
        // a terminal state, so surface it as a failure rather than leaving the
        // "generating" toast hanging.
        notifyDismissible(NotificationType.error, defaultErrorMessage);
        return;
      }
      track(id, fallbackNameRef.current);
    },
    onError: (error) => {
      notifyDismissible(NotificationType.error, backendDetail(error));
    },
  });

  // A fresh request (new object reference) submits a new export, once: it is
  // consumed from the store on submit so a remount of this controller finds
  // nothing pending rather than re-posting the same export.
  useEffect(() => {
    if (!request) {
      return;
    }
    fallbackNameRef.current = request.fallbackName;
    levelRef.current = request.level;
    startExport({
      level: request.level,
      orgCode: request.orgCode,
      filters: request.filters,
      metricIds: request.metricIds,
    });
    setExportRequest(undefined);
    // Intentionally keyed on the request reference alone; `startExport` and
    // `setExportRequest` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  return null;
}
